import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AttachMoney, Person, Business } from '@mui/icons-material';

const TopOpportunities = () => {
  const [sortOrder, setSortOrder] = useState('desc');
  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => {
    // Simulate opportunities based on CSV data structure
    const mockOpportunities = [
      {
        id: 1,
        customerName: 'Khaled Al-Maani',
        customerId: '101136',
        opportunity: 'Credit Card Upgrade',
        potentialValue: 45000,
        probability: 'High',
        type: 'credit',
        lastActivity: '2 days ago'
      },
      {
        id: 2,
        customerName: 'Nader Al-Masri',
        customerId: '101982',
        opportunity: 'Term Deposit Investment',
        potentialValue: 120000,
        probability: 'Medium',
        type: 'investment',
        lastActivity: '1 day ago'
      },
      {
        id: 3,
        customerName: 'Mahmoud Al-Jundi',
        customerId: '102874',
        opportunity: 'Insurance Package',
        potentialValue: 85000,
        probability: 'High',
        type: 'insurance',
        lastActivity: '3 days ago'
      },
      {
        id: 4,
        customerName: 'Amira Al-Kurdi',
        customerId: '103926',
        opportunity: 'Personal Loan',
        potentialValue: 65000,
        probability: 'Medium',
        type: 'loan',
        lastActivity: '5 days ago'
      },
      {
        id: 5,
        customerName: 'Yousef Al-Zoubi',
        customerId: '101915',
        opportunity: 'Investment Portfolio',
        potentialValue: 95000,
        probability: 'High',
        type: 'investment',
        lastActivity: '1 week ago'
      }
    ];

    setOpportunities(mockOpportunities);
  }, []);

  const handleSort = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    
    const sortedOpportunities = [...opportunities].sort((a, b) => {
      return newOrder === 'asc' ? a.potentialValue - b.potentialValue : b.potentialValue - a.potentialValue;
    });
    
    setOpportunities(sortedOpportunities);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'credit':
        return <AttachMoney className="text-[#B4E50D]" />;
      case 'investment':
        return <TrendingUp className="text-[#B4E50D]" />;
      case 'insurance':
        return <Business className="text-[#B4E50D]" />;
      case 'loan':
        return <Person className="text-[#B0B0B0]" />;
      default:
        return <TrendingUp className="text-[#B4E50D]" />;
    }
  };

  const getProbabilityColor = (probability) => {
    switch (probability) {
      case 'High':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Medium':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Low':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const totalValue = opportunities.reduce((sum, opp) => sum + opp.potentialValue, 0);

  return (
    <div className="bg-[#1A1A1A] rounded-xl p-6 shadow-xl border border-[#2A2A2A] hover:border-[#B4E50D] transition-all duration-300">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-[#FFFFFF]">Top Opportunities</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSort}
            className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#FFFFFF] transition-colors"
            title={`Sort by ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
          >
            {sortOrder === 'asc' ? <TrendingUp /> : <TrendingDown />}
          </button>
          <div className="bg-[#B4E50D] p-3 rounded-xl">
            <TrendingUp className="text-[#0A0A0A]" />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mb-6 p-4 bg-[#2A2A2A] rounded-xl border border-[#3A3A3A]">
        <div className="text-center">
          <div className="text-3xl font-bold text-[#B4E50D]">${totalValue.toLocaleString()}</div>
          <div className="text-sm text-[#B0B0B0]">Total Potential Value</div>
        </div>
      </div>
      
      <div className="space-y-3 max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#3A3A3A] scrollbar-track-[#2A2A2A] hover:scrollbar-thumb-[#B4E50D]">
        {opportunities.map((opportunity) => (
          <div 
            key={opportunity.id} 
            className="p-3 rounded-xl bg-[#2A2A2A] hover:bg-[#3A3A3A] transition-all duration-200 cursor-pointer group border border-[#2A2A2A] hover:border-[#3A3A3A]"
          >
            <div className="flex items-start space-x-3">
              <div className="mt-1">
                {getTypeIcon(opportunity.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-[#FFFFFF] text-sm">{opportunity.customerName}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getProbabilityColor(opportunity.probability)}`}>
                    {opportunity.probability}
                  </span>
                </div>
                <p className="text-[#B0B0B0] text-sm mt-1">{opportunity.opportunity}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg font-bold text-[#B4E50D]">
                    ${opportunity.potentialValue.toLocaleString()}
                  </span>
                  <span className="text-xs text-[#B0B0B0]">{opportunity.lastActivity}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
        <button className="w-full py-3 px-4 bg-[#B4E50D] text-[#0A0A0A] rounded-xl font-medium hover:shadow-lg transition-all duration-200 hover:transform hover:scale-105">
          View All Opportunities
        </button>
      </div>
    </div>
  );
};

export default TopOpportunities;